import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReactiveFormsModule} from '@angular/forms';
import {Store} from '@ngrx/store';
import {FormDialog, FormDialogDefinition} from '@aviary/dialogs/form-dialog/form-dialog.types';
import {FormDialogComponent} from '@aviary/dialogs/form-dialog/form-dialog.component';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {DateFormFieldComponent} from '@aviary/forms/fields/date-form-field/date-form-field.component';
import {SelectFormFieldComponent} from '@aviary/forms/fields/select-form-field/select-form-field.component';
import {HoldDetailComponent} from '@app/my-account/holds/hold-detail/hold-detail.component';
import {HoldsActions} from '@store/holds/holds.actions';
import {EditHoldValue, Hold, HoldDialogData} from '@store/holds/holds.types';
import {HoldsValidators} from '@store/holds/holds.validators';
import {BranchSelectors} from '@store/branches/branch.selectors';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {OutlinedButtonComponent} from '@aviary/buttons/outlined-button/outlined-button.component';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';

@Component({
  selector: 'px-edit-hold-dialog',
  templateUrl: './edit-hold.dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DateFormFieldComponent, FormDialogComponent, HoldDetailComponent, ReactiveFormsModule, SelectFormFieldComponent,
    SelectPipe, FormatPipe, OutlinedButtonComponent, TextButtonComponent
  ]
})
export class EditHoldDialog implements FormDialog<HoldDialogData, EditHoldValue> {
  private readonly dialogRef: MatDialogRef<EditHoldDialog> = inject(MatDialogRef);
  private readonly store = inject(Store);
  public readonly pickupBranchSelector = BranchSelectors.PickupLocations.pickupLocationsSelectOptions;
  public readonly dialogData: HoldDialogData = inject(MAT_DIALOG_DATA);
  public readonly hold: Hold = this.dialogData.hold;
  public readonly editHoldForm: ExtendedFormGroup = buildFormGroup({
    pickupBranch: this.hold.pickupBranchId,
    pauseUntil: this.hold.notWantedBefore ?? ''
  }, inject(HoldsValidators).editHold);
  public readonly pauseUntilDateControl = getExtendedControlFrom(this.editHoldForm, 'pauseUntil');

  public readonly dialogDefinition: FormDialogDefinition<EditHoldValue> = {
    title: 'Edit Hold',
    confirmButtonLabel: 'Save',
    formGroup: this.editHoldForm
  };

  public onClickCancelHold(): void {
    this.store.dispatch(HoldsActions.showCancelHold(this.hold));
    this.dialogRef.close();
  }

  public onClickResumeHold(): void {
    this.pauseUntilDateControl.patchValueAndMarkAsDirty('');
  }

  public get isPaused(): boolean {
    return isPopulatedString(this.pauseUntilDateControl.value);
  }
}
